import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { t } from "@/game/i18n";
import { DailyEntry, fetchDailyTop } from "@/game/api";
import { Platform } from "@/game/storage";

const fmtTime = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

export const DailyLeaderboard = ({ platform: initial, highlightId, refreshKey = 0 }: { platform: Platform; highlightId?: string; refreshKey?: number }) => {
  const [platform, setPlatform] = useState<Platform>(initial);
  const [scope, setScope] = useState<"day" | "all">("day");
  const [rows, setRows] = useState<DailyEntry[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false); setRows(null);
    fetchDailyTop(platform, scope).then(setRows).catch(() => setError(true));
  }, [platform, scope, refreshKey]);

  const tab = (on: boolean) => `lang-btn ${on ? "lang-btn-on" : ""}`;
  return (
    <div data-testid="daily-leaderboard" className="w-full">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <CalendarDays size={16} className="text-sky-300" />
        <span className="eyebrow">{t("daily")}</span>
        <span className="flex gap-1 ml-auto" role="group">
          <button type="button" data-testid="daily-scope-day" className={tab(scope === "day")} onClick={() => setScope("day")}>{t("today")}</button>
          <button type="button" data-testid="daily-scope-all" className={tab(scope === "all")} onClick={() => setScope("all")}>{t("allTime")}</button>
        </span>
        <span className="flex gap-1" role="group">
          <button type="button" data-testid="daily-platform-desktop" className={tab(platform === "desktop")} onClick={() => setPlatform("desktop")}>{t("desktopLb")}</button>
          <button type="button" data-testid="daily-platform-mobile" className={tab(platform === "mobile")} onClick={() => setPlatform("mobile")}>{t("mobileLb")}</button>
        </span>
      </div>
      {error && <div data-testid="daily-leaderboard-error" className="text-sm text-red-400">{t("submitFailed")}</div>}
      {!error && rows === null && <div data-testid="daily-leaderboard-loading" className="text-sm text-stone-500">{t("loading")}</div>}
      {rows && rows.length === 0 && <div data-testid="daily-leaderboard-empty" className="text-sm text-stone-500">{t("noScores")}</div>}
      {rows && rows.length > 0 && (
        <ol data-testid="daily-leaderboard-list" className="space-y-1">
          {rows.map((r, i) => (
            <li key={r.id} data-testid={`daily-row-${i + 1}`} className={`flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm ${r.id === highlightId ? "bg-sky-500/25 ring-2 ring-sky-400 text-sky-100" : "bg-white/[0.03]"}`}>
              <span className="font-mono text-sky-300/80 w-5 text-right">{i + 1}</span>
              <span className="flex-1 truncate font-semibold text-stone-200">{r.name}</span>
              <span className="text-xs text-stone-500">{t("wave")} {r.wave} · {fmtTime(r.seconds)}</span>
              <span className="font-mono font-bold text-sky-200">{r.score}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
